import React, { useState, useEffect } from 'react';
import uniqid from 'uniqid';

import Loader from 'components/partials/loader/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import RelatedContent from 'containers/user/learn/components/related-content/related-content';

import api from 'api';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { getBlocksFromContent } from 'containers/user/learn/heplers/get-blocks-from-content';
import {
  getIsRelatedContentShown,
  getRelatedContent,
} from 'containers/user/learn/heplers/content-builder-helpers';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Heading from 'components/partials/heading/heading';
import Author from 'containers/user/learn/components/author/author';
import { formatDate } from 'helpers/dates-formatter';
import Icons from 'assets/icons';

interface PresenterAvatar {
  data?: { attributes?: { url?: string } };
}

interface Presenter {
  presenterName?: string;
  presenterPosition?: string;
  presenterAvatar?: PresenterAvatar;
}

interface WebinarData {
  name?: string;
  webinarDate?: string;
  presenters?: Presenter[];
  content?: Array<{
    id: string | number;
    type: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

interface WebinarProps {
  webinarId: string | number;
}

const Webinar = ({ webinarId }: WebinarProps): React.JSX.Element => {
  const [webinar, setWebinar] = useState<WebinarData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [presenters, setPresenters] = useState<Presenter[]>([]);

  const user = useCurrentUser();

  useEffect(() => {
    setLoading(true);
    api
      .getWebinar(webinarId, user?.data?.oktaUserId ?? '')
      .then((res) => {
        setWebinar(
          (res as { data: { data: { attributes: WebinarData } } }).data.data
            .attributes
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!!webinar?.presenters?.length) {
      setPresenters(webinar.presenters);
    }
  }, [webinar]);

  return loading ? (
    <Loader {...{ loading }} />
  ) : (
    <ResponsiveWrapper customClass="flex flex-col">
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Learn', href: '/learn' },
          { label: 'Webinars', href: '/learn/content/webinars' },
        ]}
        customClass="!mt-5"
      />
      <Heading
        variant="h1"
        customClass="text-4xl font-semibold mobile:pt-4 xs:pt-6 mb-2"
      >
        {webinar.name}
      </Heading>

      <div className="flex items-center text-navy-500 mb-4">
        {webinar.webinarDate && (
          <>
            <Icons.Calendar className="h-4 w-4 mr-1" />
            <div className="text-sm">
              {formatDate(webinar.webinarDate, 'MMM D, YYYY')}
            </div>
          </>
        )}
      </div>

      {!!presenters?.length && (
        <>
          <h2 className="text-neutral-950 font-semibold mb-2">Presenters:</h2>
          <div className="flex mobile:gap-5 flex-wrap mb-10">
            {presenters.map(
              ({ presenterName, presenterPosition, presenterAvatar }) => (
                <Author
                  key={`${presenterName}-${presenterPosition}`}
                  {...{
                    name: presenterName,
                    position: presenterPosition,
                    avatar: presenterAvatar?.data?.attributes?.url,
                  }}
                />
              )
            )}
          </div>
        </>
      )}

      {webinar.content?.map((component) =>
        getBlocksFromContent({ ...component, key: uniqid() })
      )}

      {/* <WasResourseHelpfulBanner relatedTags={webinar.relatedTags} /> */}

      {getIsRelatedContentShown(webinar as never, 'webinarIsLinkedTo') && (
        <RelatedContent
          content={
            getRelatedContent(webinar as never, 'webinarIsLinkedTo') as never
          }
        />
      )}
    </ResponsiveWrapper>
  );
};

export default Webinar;
